import i18n from '../i18n'
import ru from '../i18n/locales/ru.json'
import kk from '../i18n/locales/kk.json'

const messageKeys = new Map()
const numericMessages = []
function indexMessages(source, prefix = '') {
  for (const [key, value] of Object.entries(source)) {
    const path = prefix + key
    if (typeof value === 'string') {
      messageKeys.set(value, path)
      // Numeric parameters can be carried across a language change without
      // accepting arbitrary server text as an interpolation parameter.
      if (value.includes('{count}') && !/\{(?!count\})/.test(value)) {
        const escaped = value.split('{count}').map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        numericMessages.push([new RegExp(`^${escaped.join('(-?\\d+(?:[.,]\\d+)?)')}$`), path])
      }
    } else if (value && typeof value === 'object') indexMessages(value, `${path}.`)
  }
}
indexMessages(ru)
indexMessages(kk)

const t = (key, params) => i18n.global.t(key, params || {})
const translateFallback = fallback => {
  if (typeof fallback !== 'string' || !fallback) return t('errors.requestFailed')
  const key = messageKeys.get(fallback) || fallback
  return i18n.global.te(key) ? t(key) : t('errors.requestFailed')
}

const rules = [
  [/^(?:This field is required\.?|This field may not be blank\.?|Обязательное поле\.?|Это поле не может быть пустым\.?)$/i, 'required'],
  [/^(?:This field may not be null\.?|Это поле не может иметь значение null\.?)$/i, 'null'],
  [/^(?:Enter a valid email address\.?|Введите правильный адрес электронной почты\.?)$/i, 'email'],
  [/^(?:Enter a valid URL\.?|Введите правильный URL\.?)$/i, 'url'],
  [/^(?:A valid (?:integer|number) is required\.?|Введите правильное (?:число|целое число)\.?)$/i, 'number'],
  [/^(?:Invalid pk .+|Invalid choice\.|.+ is not a valid choice\.)$/i, 'invalid'],
  [/^(?:.+ with this .+ already exists\.?|.+ с таким .+ уже существует\.?)$/i, 'unique'],
  [/^This password is too common\.?$/i, 'passwordCommon'],
  [/^This password is entirely numeric\.?$/i, 'passwordNumeric'],
  [/^The password is too similar to .+\.?$/i, 'passwordSimilar'],
  [/^(?:Passwords? (?:do not|don't) match\.?|Пароли не совпадают\.?)$/i, 'passwordMismatch'],
  [/^(?:Incorrect password\.?|Неверный текущий пароль\.?)$/i, 'incorrectPassword'],
  [/^(?:No active account found with the given credentials|Unable to log in with provided credentials\.?)$/i, 'invalidCredentials'],
  [/^(?:Authentication credentials were not provided\.?|Invalid token\.?|Session expired\.?)$/i, 'unauthorized'],
  [/^(?:You do not have permission to perform this action\.?|Permission denied\.?)$/i, 'forbidden'],
  [/^Not found\.?$/i, 'notFound'],
  [/^(?:NCALayer не запущен или недоступен|Ошибка подключения к NCALayer|Не удалось создать подключение к NCALayer|NCALayer не подключен)$/, 'ncaConnection'],
  [/^NCALayer уже обрабатывает другой запрос$/, 'ncaBusy'],
  [/^Таймаут (?:подключения к NCALayer|ожидания ответа от NCALayer)$/, 'ncaTimeout'],
  [/^NCALayer не вернул подпись после ввода пароля$/, 'ncaNoSignature'],
  [/^(?:ACTION_CANCELLED_BY_USER|USER_CANCELLED|Операция отменена пользователем)$/, 'ncaCancelled'],
  [/^Заключение еще не создано для данного приема$/, 'conclusionNotReady'],
  [/^У вас нет доступа к этому заключению$/, 'conclusionDenied'],
  [/^Заключение не найдено$/, 'conclusionNotReady'],
  [/^Ошибка при загрузке заключения$/, 'loadConclusion'],
  [/^Не удалось получить ссылку на видеокомнату\.$/, 'videoLinkMissing'],
  [/^(?:Network Error|Failed to fetch|Load failed)$/i, 'network'],
  [/^(?:timeout of \d+ms exceeded|The operation was aborted\.?)$/i, 'timeout'],
  [/^(?:Ensure this field has at least (\d+) characters.*|This password is too short\. It must contain at least (\d+) characters\.)$/i, 'minLength'],
  [/^Ensure this field has no more than (\d+) characters.*$/i, 'maxLength'],
  [/^Ensure this value is greater than or equal to (-?\d+(?:\.\d+)?)\.?$/i, 'minValue'],
  [/^Ensure this value is less than or equal to (-?\d+(?:\.\d+)?)\.?$/i, 'maxValue']
]

function knownMessage(value) {
  if (typeof value !== 'string') return ''
  const text = value.trim()
  const key = messageKeys.get(text)
  if (key) return t(key)
  if (text.startsWith('errors.') && i18n.global.te(text)) return t(text)
  for (const [pattern, key] of numericMessages) {
    const match = text.match(pattern)
    if (match) return t(key, { count: match[1] })
  }
  for (const [pattern, key] of rules) {
    const match = text.match(pattern)
    if (match) return t(`errors.${key}`, { count: match[1] || match[2] })
  }
  return ''
}

const generalFields = new Set(['detail', 'error', 'message', 'errors', 'non_field_errors', 'user', 'profile'])
const ignoredFields = new Set(['code', 'status', 'status_code', 'traceback', 'stack', 'request_id', 'correlation_id'])

function fieldLabel(path) {
  const field = path.split('.').filter(part => !/^\d+$/.test(part) && !generalFields.has(part)).pop()
  const key = `errors.fields.${field}`
  return field && i18n.global.te(key) ? t(key) : ''
}

function collectMessages(data, path = '', result = [], depth = 0, withLabels = true) {
  if (depth > 8 || result.length >= 20 || data == null) return result
  if (typeof data === 'string') {
    // Do not display unknown server strings, HTML responses or exception details.
    const message = knownMessage(data) || t(path ? 'errors.invalid' : 'errors.validation')
    const label = withLabels ? fieldLabel(path) : ''
    result.push(label ? `${label}: ${message}` : message)
  } else if (Array.isArray(data)) {
    data.slice(0, 20).forEach(value => collectMessages(value, path, result, depth + 1, withLabels))
  } else if (typeof data === 'object') {
    for (const [field, value] of Object.entries(data)) {
      if (ignoredFields.has(field)) continue
      if (typeof value !== 'string' && (!value || typeof value !== 'object')) continue
      collectMessages(value, path ? `${path}.${field}` : field, result, depth + 1, withLabels)
    }
  }
  return result
}

/** Localize a displayed error, including an error that was set before switching languages. */
export function localizeErrorMessage(value, fallback = 'errors.requestFailed') {
  if (value == null || value === '') return ''
  if (typeof value === 'string') {
    const known = knownMessage(value)
    if (known) return known
    const parts = value.split(/\n| · /).filter(Boolean)
    if (parts.length > 1) return [...new Set(parts.map(part => localizeErrorMessage(part, fallback)))].join('\n')
    // Handle field-labelled messages produced by the API formatter, including
    // a locale change after a response has already been displayed.
    const separator = value.indexOf(': ')
    if (separator !== -1) {
      const label = knownMessage(value.slice(0, separator))
      const message = knownMessage(value.slice(separator + 2))
      if (label && message) return `${label}: ${message}`
    }
    return translateFallback(fallback)
  }
  if (value instanceof Error || value.response) return getErrorMessage(value, fallback)
  const messages = collectMessages(value)
  return [...new Set(messages)].join('\n') || translateFallback(fallback)
}

/** Keep the original error/response intact for status checks, retries and logging. */
export function getErrorMessage(error, fallback = 'errors.requestFailed') {
  if (!error) return translateFallback(fallback)
  if (typeof error === 'string' || Array.isArray(error)) return localizeErrorMessage(error, fallback)
  if (error.code === 'ERR_CANCELED' || error.name === 'AbortError') return ''
  const status = Number(error.response?.status || error.status)
  if (status >= 500) return t('errors.server')
  if (status === 401) {
    return /(?:^|\/)login\/?(?:\?|$)/.test(error.config?.url || '') ? t('errors.invalidCredentials') : t('errors.unauthorized')
  }
  if (status === 403) return t('errors.forbidden')
  if (status === 404) return t('errors.notFound')
  if (status === 409) return t('errors.conflict')
  if (status === 413) return t('errors.fileTooLarge')
  if (status === 429) return t('errors.tooManyRequests')
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.name === 'TimeoutError') return t('errors.timeout')
  if (!error.response && (error.request || error.code === 'ERR_NETWORK' || /^(Network Error|Failed to fetch|Load failed)$/.test(error.message))) return t('errors.network')
  const data = error.response?.data
  if (data && typeof data === 'object') {
    const messages = collectMessages(data)
    if (messages.length) return [...new Set(messages)].join('\n')
  }
  return knownMessage(typeof data === 'string' ? data : error.message) || translateFallback(fallback)
}

/** Preserve nested field paths for inline form feedback (e.g. user.email). */
export function getFieldErrors(error) {
  const data = error?.response?.data
  const result = {}
  if (!data || typeof data !== 'object' || Array.isArray(data) || ![400, 422].includes(Number(error.response.status))) return result
  function visit(value, path, depth = 0) {
    if (depth > 8 || ignoredFields.has(path.split('.').pop())) return
    if (typeof value === 'string' || Array.isArray(value)) {
      const messages = collectMessages(value, path, [], depth, false)
      if (messages.length) result[path] = [...new Set(messages)].join('\n')
    } else if (value && typeof value === 'object') {
      Object.entries(value).forEach(([key, nested]) => visit(nested, path ? `${path}.${key}` : key, depth + 1))
    }
  }
  Object.entries(data).forEach(([field, value]) => visit(value, field))
  return result
}
